/**
 * Remembers the last enquiry the user actually navigated to, in memory only — no browser
 * storage (CLAUDE.md forbids it), and lost on a hard reload by design.
 *
 * This is the ONLY place `lastEnquiryId` is written, and it is only ever written from a real
 * route param (see `Layout`'s effect) — never a fabricated default. See `lib/activeEnquiry.ts`
 * for the pure decision of what the flow screens do with it.
 */

import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'

interface ActiveEnquiryContextValue {
  lastEnquiryId: string | null
  setLastEnquiryId: (id: string) => void
}

const ActiveEnquiryContext = createContext<ActiveEnquiryContextValue | null>(null)

export function ActiveEnquiryProvider({ children }: { children: ReactNode }) {
  const [lastEnquiryId, setLastEnquiryId] = useState<string | null>(null)

  const value = useMemo<ActiveEnquiryContextValue>(
    () => ({ lastEnquiryId, setLastEnquiryId }),
    [lastEnquiryId],
  )

  return <ActiveEnquiryContext.Provider value={value}>{children}</ActiveEnquiryContext.Provider>
}

export function useActiveEnquiry(): ActiveEnquiryContextValue {
  const ctx = useContext(ActiveEnquiryContext)
  if (!ctx) throw new Error('useActiveEnquiry must be used inside <ActiveEnquiryProvider>')
  return ctx
}
